import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function HealthWidget() {
  const [data, setData] = useState({ status: "loading" });
  const [open, setOpen] = useState(false);
  const failRef = useRef(0);
  const poll = () => {
    fetch(`${BACKEND_URL}/api/health`)
      .then((r) => r.json().then((j) => ({ http: r.status, ...j })))
      .then((j) => { failRef.current = 0; setData(j); })
      .catch(() => {
        // Tolerate transient network blips: only flag unreachable after 2
        // consecutive failures, otherwise keep the last known-good status.
        failRef.current += 1;
        if (failRef.current >= 2) {
          setData({ status: "unreachable", http: 0, mongo: false, storage: false, redis: false });
        }
      });
  };
  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, []);

  // Overall status is driven ONLY by required deps (Mongo + Object storage).
  // Redis is the optional WebSocket bus (single-pod mode runs fine without it),
  // so a null/false Redis never degrades the overall health.
  const requiredOk = data.mongo === true && data.storage === true;
  const overall =
    data.status === "loading" ? "loading" :
    data.status === "unreachable" ? "unreachable" :
    requiredOk ? "ok" : "degraded";
  const colorFor = (v) => v === true ? "#10b981" : v === false ? "#ef4444" : "#9ca3af";
  const dotStyle = (v) => ({ background: colorFor(v), width: 10, height: 10, borderRadius: 999, display: "inline-block", border: "2px solid #000" });
  const headDot =
    overall === "ok" ? "#10b981" :
    overall === "loading" ? "#9ca3af" : "#ef4444";

  return (
    <div className="relative">
      <button
        data-testid="health-widget"
        onClick={() => setOpen((o) => !o)}
        className="brutal-btn-secondary inline-flex items-center gap-2"
        title="System health"
      >
        <span style={{ background: headDot, width: 10, height: 10, borderRadius: 999, display: "inline-block", border: "2px solid #000" }}/>
        <span className="font-black uppercase text-xs">Health</span>
      </button>
      {open && (
        <div data-testid="health-popover" className="absolute right-0 top-[110%] z-50 w-64 border-4 border-black bg-white p-3 brutal-shadow-sm">
          <div className="font-black uppercase text-xs mb-2 flex items-center justify-between">
            <span>System Health</span>
            <span data-testid="health-status" className="font-mono-bq" style={{ color: headDot }}>{overall}</span>
          </div>
          <ul className="text-sm space-y-1.5">
            <li className="flex items-center justify-between"><span>MongoDB</span><span data-testid="health-mongo" style={dotStyle(data.mongo)}/></li>
            <li className="flex items-center justify-between"><span>Object storage</span><span data-testid="health-storage" style={dotStyle(data.storage)}/></li>
            <li className="flex items-center justify-between"><span>Redis (WebSocket bus)</span><span data-testid="health-redis" style={dotStyle(data.redis === null ? null : data.redis)}/></li>
          </ul>
          <p className="text-[10px] text-neutral-500 mt-2 font-mono">
            HTTP {data.http ?? "—"} · auto-refresh every 30s
            {data.redis === null && (<><br/>Redis optional — single-pod mode (not required)</>)}
          </p>
        </div>
      )}
    </div>
  );
}

export default HealthWidget;
